import { useState } from 'react';

import { Stack, Typography } from '@mui/material';

import { say } from '@/james-playground/aa-family-claim/claim/asks';
import {
  text,
  type Value,
} from '@/james-playground/aa-family-claim/claim/facts';
import { entry } from '@/james-playground/aa-family-claim/claim/registry';
import { SIGNER } from '@/james-playground/aa-family-claim/claim/cards/cards.content';
import {
  CardShell,
  ChoiceCards,
  EntryField,
  hasProblem,
  Note,
  Part,
  Parts,
  Toggle,
  useDraft,
  useQids,
} from '@/james-playground/aa-family-claim/claim/cards/parts';
import { type CardProps } from '@/james-playground/aa-family-claim/claim/cards/types';
import {
  goesAsUnsure,
  shows,
  signerOutcome,
  signerStateFrom,
  signerValues,
  storedAs,
  str,
  type Authority,
  type PoaType,
  type SignerState,
  type Unsure,
  type Who,
} from '@/james-playground/aa-family-claim/claim/cards/values';

const DETAILS = [
  'representativeOrganisation',
  'representativeName',
  'representativeNiNumber',
  'representativeDob',
  'representativeAddress',
  'representativePostcode',
  'representativePhone',
];

/**
 * Who signs the form (spec 5). She signs; someone with Power of Attorney for
 * her property and money; someone else with legal authority; or not sure,
 * with a short explainer and the way to apply to be appointed. A Health and
 * Welfare or unregistered Power of Attorney cannot sign, so the card says why
 * and carries on as "not sure". The signer's own details come from the
 * account, to check. The product never judges whether she can sign: it
 * records what the family says.
 */
const SignerCard = ({ ask, facts, voice: v, onSubmit, onSkip }: CardProps) => {
  const { draft, set } = useDraft(ask, facts);
  const qid = useQids();
  const [s, setS] = useState<SignerState>(() => signerStateFrom(facts));
  const quicker = text(facts, 'specialRules') === 'Yes';

  if (v.selfClaim)
    return (
      <CardShell
        label={say(ask.title, v)}
        onSkip={onSkip}
        onContinue={() =>
          onSubmit(
            { signingForSomeoneElse: 'No' },
            say(SIGNER.reply.herself, v),
          )
        }
      >
        <Typography variant='body1'>{SIGNER.selfClaim}</Typography>
      </CardShell>
    );

  const outcome = signerOutcome(s);
  const decided = outcome && outcome !== 'adviser' ? signerValues(outcome) : {};
  const live = { ...draft, ...decided };
  const showing = (id: string) => shows(id, ask.entries, facts, live);
  const details =
    outcome && outcome !== 'adviser' && outcome.someoneElse === 'Yes'
      ? DETAILS.filter(showing)
      : [];
  const online =
    s.who === 'attorney' && s.poa === 'finance' && showing('poaOnlineAccount');

  const ready =
    outcome !== undefined &&
    (!online || Boolean(str(draft.poaOnlineAccount))) &&
    !details.some((id) => hasProblem(id, draft[id]));

  const submit = () => {
    if (!outcome) return;
    if (outcome === 'adviser') return onSkip();
    const values: Record<string, Value> = { ...decided };
    if (online) values.poaOnlineAccount = str(draft.poaOnlineAccount);
    for (const id of details) {
      const value = storedAs(entry(id).answerType, str(draft[id]));
      if (value) values[id] = value;
    }
    const key =
      outcome.someoneElse === 'No'
        ? 'herself'
        : (outcome.category ?? 'herself');
    onSubmit(values, say(SIGNER.reply[key], v));
  };

  const choose = (who: Who) => setS({ who });
  const whoOptions: { value: Who; label: string }[] = [
    { value: 'herself', label: say(SIGNER.who.herself, v) },
    ...(quicker
      ? [{ value: 'quicker' as const, label: SIGNER.who.quicker }]
      : []),
    { value: 'attorney', label: SIGNER.who.attorney },
    { value: 'authority', label: SIGNER.who.authority },
    { value: 'unsure', label: SIGNER.who.unsure },
  ];

  return (
    <CardShell
      label={say(ask.title, v)}
      ready={ready}
      onContinue={submit}
      onSkip={onSkip}
    >
      <ChoiceCards
        label={say(ask.prompt, v)}
        options={whoOptions}
        selected={s.who ? [s.who] : []}
        onPick={(who) => choose(who as Who)}
      />
      <Parts>
        {s.who === 'quicker' && (
          <Note>
            <Typography variant='body2'>
              {say(SIGNER.quickerNote, v)}
            </Typography>
          </Note>
        )}

        {s.who === 'attorney' && (
          <Part qid={qid('poa')} question={SIGNER.poaQuestion}>
            <ChoiceCards
              labelledBy={qid('poa')}
              options={(Object.keys(SIGNER.poa) as PoaType[]).map((k) => ({
                value: k,
                label: SIGNER.poa[k],
              }))}
              selected={s.poa ? [s.poa] : []}
              onPick={(poa) => setS({ who: 'attorney', poa: poa as PoaType })}
            />
          </Part>
        )}
        {online && (
          <Stack spacing={1.25}>
            <Toggle
              question={SIGNER.onlineQuestion}
              value={str(draft.poaOnlineAccount)}
              onChange={(value) => set('poaOnlineAccount', value)}
            />
            {SIGNER.online[str(draft.poaOnlineAccount)] && (
              <Note>
                <Typography variant='body2'>
                  {SIGNER.online[str(draft.poaOnlineAccount)]}
                </Typography>
              </Note>
            )}
          </Stack>
        )}
        {s.who === 'attorney' &&
          (s.poa === 'welfare' || s.poa === 'unregistered') && (
            <Note tone='warning'>
              <Typography variant='body2'>{SIGNER.poaCannot[s.poa]}</Typography>
            </Note>
          )}

        {s.who === 'authority' && (
          <Part qid={qid('authority')} question={SIGNER.authorityQuestion}>
            <ChoiceCards
              labelledBy={qid('authority')}
              options={(Object.keys(SIGNER.authority) as Authority[]).map(
                (k) => ({
                  value: k,
                  label: SIGNER.authority[k],
                  detail: (
                    <Typography variant='body2'>
                      {SIGNER.authorityNote[k]}
                    </Typography>
                  ),
                }),
              )}
              selected={s.authority ? [s.authority] : []}
              onPick={(authority) =>
                setS({ who: 'authority', authority: authority as Authority })
              }
            />
          </Part>
        )}

        {goesAsUnsure(s) && (
          <Stack spacing={1.5}>
            <Stack spacing={0.75}>
              {SIGNER.explainer.map((line) => (
                <Typography key={line} variant='body2'>
                  {say(line, v)}
                </Typography>
              ))}
            </Stack>
            <ChoiceCards
              label={SIGNER.who.unsure}
              options={(Object.keys(SIGNER.unsure) as Unsure[]).map((k) => ({
                value: k,
                label: SIGNER.unsure[k],
              }))}
              selected={s.unsure ? [s.unsure] : []}
              onPick={(unsure) => setS({ ...s, unsure: unsure as Unsure })}
            />
          </Stack>
        )}

        {details.length > 0 && (
          <Stack spacing={1.5} role='group' aria-labelledby={qid('details')}>
            <Stack spacing={0.25}>
              <Typography id={qid('details')} variant='h6'>
                {SIGNER.details}
              </Typography>
              <Typography variant='body2'>{SIGNER.detailsNote}</Typography>
            </Stack>
            {details.map((id) => (
              <EntryField
                key={id}
                id={id}
                v={v}
                value={str(draft[id])}
                onChange={(value) => set(id, value)}
              />
            ))}
          </Stack>
        )}
      </Parts>
    </CardShell>
  );
};

SignerCard.displayName = 'SignerCard';
export default SignerCard;
